import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrShift } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

export function HrShiftSelect({
  label = 'Shift',
  value = '',
  onChange,
  placeholder,
  className,
  required,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('hr');
  const [shifts, setShifts] = useState<HrShift[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listShifts()
      .then(setShifts)
      .catch((err) => showApiError(err, t('errors.failedToLoadShifts')))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      shifts
        .filter((shift) => shift.active !== false)
        .map((shift) => ({
        value: String(shift.id),
        label: shift.startTime && shift.endTime
          ? `${shift.name} (${shift.startTime}–${shift.endTime})`
          : shift.name,
      })),
    [shifts],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectShift}
      loading={loading}
      searchPlaceholder={selectLabels.searchShifts}
      emptyMessage={selectLabels.noShifts}
    />
  );
}
